/**
 * O desenho interno do componente — escolhido no acervo, pelo nome.
 *
 * Até aqui ele só existia como um campo numérico na lista de componentes do
 * painel: quem não sabia o id de cor não achava a ferramenta (relatório de
 * 28/09/2026). Agora mora no painel do componente, na montagem, com busca pelo
 * título nos 2.745 desenhos do acervo.
 *
 * **Sem prévia do traço aqui, e é de propósito.** O desenho interno é gravado
 * como gabarito de texto, não como SVG — o traço só existe depois que o motor o
 * encaixa no componente. A prévia é o próprio bloco da montagem, que se
 * redesenha ao gravar. O que a lista mostra é o que ajuda a escolher: pinos de
 * cada lado, posição e em quantos diagramas o desenho já serve.
 *
 * **A busca fica no lado do componente.** O desenho é traçado para um lado:
 * componente na coluna da esquerda do principal usa desenho "left", e um
 * "right" ali sai espelhado, para fora do componente. Misturar os dois lados
 * dobrava a lista com opções que não servem.
 */

import { useEffect, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { listarDesenhosInternos, obterDesenhoInterno } from "@/api/desenhosInternos"

const POSICAO: Record<string, string> = { left: "esquerda", right: "direita", center: "centro" }

export function SeletorDeDesenhoInterno({ valor, aoMudar, posicao }: {
  valor: number
  aoMudar: (id: number) => void
  /** O lado do componente, "left" ou "right". Outro valor (ou ausente) procura nos dois. */
  posicao?: string
}) {
  const lado = posicao === "left" || posicao === "right" ? posicao : ""
  const [texto, setTexto] = useState("")
  const [termo, setTermo] = useState("")
  const espera = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(espera.current), [])

  function digitar(v: string) {
    setTexto(v)
    window.clearTimeout(espera.current)
    // Busca no servidor com 2 caracteres e 400 ms, como as outras buscas grandes (regra 2).
    espera.current = window.setTimeout(() => setTermo(v.trim().length >= 2 ? v.trim() : ""), 400)
  }

  const atual = useQuery({
    queryKey: ["desenho-interno", valor] as const,
    enabled: valor > 0,
    queryFn: () => obterDesenhoInterno(valor),
    staleTime: 5 * 60_000,
  })

  const achados = useQuery({
    queryKey: ["desenhos-internos-busca", termo, lado] as const,
    enabled: termo !== "",
    queryFn: () => listarDesenhosInternos({ busca: termo, posicao: lado, porPagina: 20 }),
  })

  const itens = achados.data?.itens ?? []

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5 }}>
        {valor > 0 ? (
          <>
            <b className="num">{valor}</b>
            <span style={{ color: "var(--txt2)" }}>
              {atual.isPending ? "carregando…" : atual.data?.titulo ?? "desenho não encontrado no acervo"}
            </span>
            <button type="button" onClick={() => aoMudar(0)}
                    style={{ marginLeft: "auto", fontSize: 11, color: "var(--txt3)", textDecoration: "underline" }}>
              tirar o desenho interno
            </button>
          </>
        ) : (
          <span style={{ color: "var(--txt3)" }}>Sem desenho interno.</span>
        )}
      </div>

      <input
        value={texto}
        onChange={(e) => digitar(e.target.value)}
        placeholder={lado
          ? `Procure no acervo pelo título — só os da ${POSICAO[lado]}, o lado deste componente…`
          : "Procure no acervo pelo título (ex.: relé, sensor, bobina)…"}
        aria-label="Procurar desenho interno"
        style={{
          padding: "5px 8px", fontSize: 11.5, border: "1px solid var(--line)", borderRadius: 5,
          background: "var(--surface)", color: "var(--txt)",
        }}
      />

      {termo && (
        <div style={{ maxHeight: 220, overflow: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
          {achados.isPending ? (
            <span style={{ fontSize: 11, color: "var(--txt3)" }}>Procurando…</span>
          ) : itens.length === 0 ? (
            <span style={{ fontSize: 11, color: "var(--txt3)" }}>
              Nenhum desenho com esse título{lado ? ` do lado ${POSICAO[lado]}` : ""}.
            </span>
          ) : (
            <>
              {itens.map((d) => (
                <button
                  key={d.id} type="button"
                  onClick={() => { aoMudar(d.id); setTexto(""); setTermo("") }}
                  disabled={d.tamanhoDoDesenho === 0}
                  title={d.tamanhoDoDesenho === 0 ? "Este desenho está vazio no acervo." : undefined}
                  style={{
                    display: "grid", gridTemplateColumns: "48px 1fr auto", gap: 8, alignItems: "center",
                    textAlign: "left", padding: "4px 7px", borderRadius: 5, fontSize: 11,
                    background: d.id === valor ? "var(--brand-soft)" : "var(--surface2)",
                    color: d.tamanhoDoDesenho === 0 ? "var(--txt3)" : "var(--txt)",
                    cursor: d.tamanhoDoDesenho === 0 ? "default" : "pointer",
                  }}
                >
                  <span className="num">{d.id}</span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {d.titulo || "(sem título)"}
                  </span>
                  <span style={{ color: "var(--txt3)", fontSize: 10.5, whiteSpace: "nowrap" }}>
                    {d.qtdPinos}/{d.qtdPinosOposto} pinos · {POSICAO[d.posicao] ?? d.posicao}
                    {" · "}{d.diagramas} diagr.
                  </span>
                </button>
              ))}
              {(achados.data?.total ?? 0) > itens.length && (
                <span style={{ fontSize: 10.5, color: "var(--txt3)", padding: "2px 7px" }}>
                  {achados.data?.total} encontrados; mostrando {itens.length}. Refine o título.
                </span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
